import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { TABLE_ROWS_COUNT } from "../../shared/constants";
import { RootState } from "../../shared/types/common";
import { Space, Button, Col, Row, Modal, Tabs } from "antd";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import { useForm } from "antd/lib/form/Form";
import CreateEditBillingCycleComponent from "./components/create-edit-billing-cycle.component";
import { deleteBillingCycleById, getBillingCycleList } from "../../core/store/reducers/billing-cycle.slice";
import { CloudDownloadOutlined, DeleteOutlined, EditOutlined, PlusOutlined, CalendarOutlined, CloseOutlined } from "@ant-design/icons";
import { BiFilterAlt } from "react-icons/bi";
import moment from "moment";
import { formatCost, showNotification } from "../../shared/utils/utils";
import { API_GET_ALL_PROJECT_LIST, API_GET_ALL_STAFF_LIST, API_GET_BILLING_CYCLE_LIST } from "../../shared/routes/private.routes";
import { Client } from "../../core/api-client";
import { ActiveFilterChipsBar } from "../../shared/components/active-filter-chips/active-filter-chips.component";
import { isFilterValueActive } from "../../shared/utils/active-filters";

const BILLING_FILTER_LABELS: Record<string, string> = {
  staffId: "Staff",
  projectId: "Project",
  month: "Month",
};

function formatBillingMonthValue(value: any): string {
  const arr = Array.isArray(value) ? value : [value];
  return arr
    .filter(Boolean)
    .map((m) => {
      if (m && typeof (m as any).format === "function") {
        return (m as any).format("MMM YYYY");
      }
      return moment(m).format("MMM YYYY");
    })
    .join(", ");
}

function billingFilterValueText(
  key: string,
  value: any,
  staffList: any[],
  projectList: any[]
): string {
  switch (key) {
    case "staffId": {
      const ids = Array.isArray(value) ? value : [value];
      return ids
        .map(
          (id: string) =>
            staffList.find((s) => s.staffId === id)?.staffName ?? id
        )
        .join(", ");
    }
    case "projectId": {
      const ids = Array.isArray(value) ? value : [value];
      return ids
        .map(
          (id: string) =>
            projectList.find((p) => p.projectId === id)?.projectName ?? id
        )
        .join(", ");
    }
    case "month":
      return formatBillingMonthValue(value);
    default:
      return String(value);
  }
}

const BILLING_FILTER_CLEAR: Record<string, any> = {
  staffId: [],
  projectId: [],
  month: [],
};

interface MonthWiseData {
    StaffName: string;
    Month: string;
    PercentAllocation: number;
    Unallocated: number;
    TotalCost: number;
}

interface ProjectWiseData {
    StaffName: string;
    Project: string;
    Client: string;
    Month: string;
    Percentage: number;
    MonthlyCost: number;
}

type BillingCycleData = MonthWiseData | ProjectWiseData;

const ManageBillingCyclePage: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [form] = useForm();
    const { loadingBillingCycle, billingCycleList, pagination } = useSelector(
        (state: RootState) => state.billingCycle
    );
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [billingCycleIdToDelete, setBillingCycleIdToDelete] = useState("");
    const [billingCycleListData, setBillingCycleListData] = useState<any[]>([]);
    const [createEditOpen, setCreateEditOpen] = useState(false);
    const [selectedEntity, setSelectedEntity] = useState<any>(null);
    const [filterVisible, setFilterVisible] = useState(false);
    const [tab, setTab] = useState("1" as string);
    const [sorter, setSorter] = useState({ sort_by: "", sort_type: "" });
    const [filters, setFilters] = useState({} as any);
    const [staffList, setStaffList] = useState<any[]>([]);
    const [projectList, setProjectList] = useState<any[]>([]);

    useEffect(()=>{
        loadList()
    }, [filters, sorter])
    const loadList = 
        (page: number | null = null) => {
            const data = {
                page: page ? page : pagination?.page ? pagination?.page : 1,
                limit: pagination?.limit || TABLE_ROWS_COUNT,
                tab: tab,
                ...sorter,
            };
            let filter: any = {};
            for (let [key, value] of Object.entries(filters)) {
                if (value !== "") {
                    filter[key] = value;
                }
            }
            dispatch(getBillingCycleList({ params: data, body: filter }));
        }
        
        const exportToCSV = async () => {
            let filter: any = {};
            const data = {
                page:  1,
                limit: pagination?.total,
                tab: tab,
                ...sorter,
            };
            for (let [key, value] of Object.entries(filters)) {
                if (value !== "") {
                    filter[key] = value;
                }
            }
            let listData = await (await Client.post(API_GET_BILLING_CYCLE_LIST, filter, {params: data})).data?.data;
            console.log('listData',listData)
            console.log('billingCycleListData',billingCycleListData)
            const dataToExport = tab === "1" 
                ? billingCycleListData.map((item) => ({
                    StaffName: item.staffName,
                    Month: moment(item.month).format("MMM YYYY"),
                    '%PercentAllocation': item.totalPercentAllocation+'%',
                    '%Unallocated': 100 - item.totalPercentAllocation+'%',
                    TotalCost: formatCost(item.totalCost),
                })) 
                : billingCycleListData.map((item) => ({
                    StaffName: item.staffName,
                    Project: item.projectName,
                    Client: item.accountName,
                    Month: moment(item.month).format("MMM YYYY"),
                    Percentage: item.percentAllocation + '%',
                    MonthlyCost: formatCost(item.monthlyCost),
                }));
        
            const csvRows: string[] = [];
            const headers = Object.keys(dataToExport[0]);
        
            csvRows.push(headers.join(",")); // Add the header row
        
            for (const row of dataToExport) {
                // Cast row as an object to avoid type errors
                csvRows.push(headers.map(header => 
                    JSON.stringify((row as Record<string, any>)[header], (key, value) => value === null ? '' : value)
                ).join(","));
            }
        
            // Create a Blob from the CSV string and create a link to download it
            const csvString = csvRows.join("\n");
            const blob = new Blob([csvString], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.setAttribute("href", url);
            a.setAttribute("download", "BILLING_CYCLE.csv");
            a.click();
            window.URL.revokeObjectURL(url);
        };
        
            
        
    const createEntityListener = () => {
        setCreateEditOpen(true);
        setSelectedEntity(null);
    };

    const editEntityListener = (entity: any) => {
        setCreateEditOpen(true);
        setSelectedEntity(entity);
    };

    useEffect(() => {
        setBillingCycleListData(
            billingCycleList.map((obj: any, index: number) => {
                const totalPercentAllocation = obj.allocations.reduce((sum: number, allocation: any) => {
                    return sum + allocation.percentAllocation;
                }, 0);
                console.log('obj',obj)
                return { key: index, ...obj, totalPercentAllocation };
            })
        );
    }, [billingCycleList]);

    const onFilterApply = (values: any) => {
        setFilters(values)
    };

    const activeFilterEntries = useMemo(() => {
        return Object.keys(filters)
            .filter((k) => isFilterValueActive(filters[k]))
            .map((key) => ({
                key,
                fieldLabel: BILLING_FILTER_LABELS[key] || key,
                valueText: billingFilterValueText(
                    key,
                    filters[key],
                    staffList,
                    projectList
                ),
            }));
    }, [filters, staffList, projectList]);

    const clearAllFilters = useCallback(() => {
        setFilters({});
        form.resetFields();
    }, [form]);

    const removeFilter = useCallback(
        (key: string) => {
            setFilters((prev: any) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
            const cleared =
                key in BILLING_FILTER_CLEAR ? BILLING_FILTER_CLEAR[key] : undefined;
            form.setFieldsValue({ [key]: cleared });
        },
        [form]
    );

    const onPageChange = (pageNumber: number) => {
        loadList(pageNumber);
    };

    const handleDelete = (billingId: string) => {
        dispatch(deleteBillingCycleById(billingId)).then((val: any) => {
            if (val?.payload?.data) {
                showNotification("Billing cycle Deleted Successfully", "success");
                loadList();
                setDeleteModalVisible(false); // Close the modal after successful deletion
            }
        });
    };

    const handleDeleteModal = (billingId: string) => {
        setDeleteModalVisible(true);
        setBillingCycleIdToDelete(billingId);
    };

    const onDeleteConfirm = () => {
        handleDelete(billingCycleIdToDelete);
    };

    const onDeleteCancel = () => {
        setDeleteModalVisible(false);
        setBillingCycleIdToDelete("");
    };

    const onChangeHandler = (_pagination: any, _filters: any, _sorter: any) => {
        if (_sorter.order === undefined) {
            setSorter({ sort_by: "", sort_type: "" });
        } else {
            let order = "";
            let columnKey = "";
            if (_sorter.order === "ascend") order = "asc";
            else order = "desc";

            if (_sorter.columnKey === "enabled") {
                columnKey = "status";
            } else if (_sorter.columnKey === "displayName") {
                columnKey = "name";
            } else {
                columnKey = _sorter.columnKey;
            }
            setSorter({ sort_by: columnKey, sort_type: order });
        }
    };

    const createEditSubmit = () => {
        setCreateEditOpen(false);
        loadList();
    };

    useEffect(() => {
        loadList();
    }, []);

    const onValuesChange = (value: any, _allValues: any) => {
    };

    useEffect(() => {
      fetchStaffList();
      fetchProjectList();
    }, []);

    const fetchStaffList = async () => {
      const response = await Client.get(API_GET_ALL_STAFF_LIST);
      if (response?.data?.data) {
        setStaffList(response?.data?.data);
      } else {
        setStaffList([]);
      }
    };

    const fetchProjectList = async () => {
      const response = await Client.get(API_GET_ALL_PROJECT_LIST);
      if (response?.data?.data) {
        setProjectList(response?.data?.data);
      } else {
        setProjectList([]);
      }
    };

    const staffDropdownOptions = staffList.map((staff) => ({
      value: staff?.staffId,
      label: staff?.staffName,
    }));

    const projectDropdownOptions = projectList.map((project) => ({
      value: project?.projectId,
      label: project?.projectName,
    }));

    const formProps = {
      form: form,
      name: "manage-billing-cycle-filters",
      enableEnterSubmit: true,
      formElements: [
        {
          name: "staffId",
          label: "",
          placeholder: "Staff Name",
          type: "Select",
          colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
          showSearch: true,
          allowClear: true,
          mode: "multiple",
          options: staffDropdownOptions,
        },
        (tab === "2") && {
            name: "projectId",
            label: "",
            placeholder: "Project",
            type: "Select",
            colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
            showSearch: true,
            allowClear: true,
            mode: "multiple",
            options: projectDropdownOptions,
          },
        {
          name: "month",
          label: "",
          placeholder: "Month",
          type: "MonthPicker",
          mode: "multiple",
          colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
          showSearch: true,
          allowClear: true,
        },
      ].filter(Boolean),
      initialValues: {
        billingCycleName: "",
      },
      extraButtons: [
        {
          htmlType: "submit",
          colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
          loading: false,
          submitButtonText: "Clear",
          type: "clearForm",
          onClick: clearAllFilters,
        },
        {
          htmlType: "submit",
          colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
          loading: false,
          submitButtonText: "Apply",
          submitButtonType: "primary",
        },
      ],
      onFinish: onFilterApply,
      onFinishFailed: null,
      onValuesChange: onValuesChange,
    };

    const headerButtons = (
        <Space size="middle" className="app-page-actions">
            <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={createEntityListener}
            >
                New Billing Cycle
            </Button>
            <Button
                shape="circle"
                type="primary"
                icon={<BiFilterAlt />}
                onClick={() => setFilterVisible(!filterVisible)}
                title={filterVisible ? "Hide filters" : "Show filters"}
            />
            <Button
                shape="circle"
                type="primary"
                icon={<CloudDownloadOutlined />}
                onClick={exportToCSV}
                title="Export CSV"
            />
        </Space>
    );

    const columnsMonthWise = [
      {
        title: "Staff Name",
        dataIndex: "staffName",
        sorter: false,
        key: "staffName",
      },
      {
        title: "Month",
        dataIndex: "month",
        sorter: false,
        key: "month",
        render: (month: string) => (month ? moment(month).format("MMM YYYY") : ""),
      },
      {
        title: "% Allocation",
        dataIndex: "totalPercentAllocation",
        sorter: false,
        key: "totalPercentAllocation",
        render: (percent: number) => `${percent}%`,
      },
      {
        title: "% Unallocated",
        dataIndex: "totalPercentAllocation",
        sorter: false,
        key: "totalPercentAllocation",
          render: (percent: number) => {
              const unallocated = 100 - percent;
              return (
                  <span style={{ color: unallocated > 0 ? "red" : "black" }}>
                      {`${unallocated}%`}
                  </span>
              );
          },
      },
      {
        title: "Total Cost",
        dataIndex: "totalCost",
        sorter: false,
        key: "totalCost",
        render: (cost: number) => formatCost(cost),
      },
      // {
      //     title: "Status",
      //     dataIndex: "status",
      //     sorter: false,
      //     key: "status",
      // },
      {
        title: "Actions",
        key: "actions",
        fixed:'right',
        render: (text: any, record: any) => (
          <Space>
            <Button
              type="link"
              onClick={() => editEntityListener(record)}
              icon={<EditOutlined />}
            ></Button>
            <Button
              type="link"
              onClick={() => handleDeleteModal(record?.billingId)}
              icon={<DeleteOutlined style={{color:"#FF0000"}} />}
            ></Button>
          </Space>
        ),
      },
    ];

    const columnsProjectWise = [
        {
            title: "Staff Name",
            dataIndex: "staffName",
            sorter: false,
            key: "staffName",
        },
        {
            title: "Project",
            dataIndex: "projectName",
            sorter: false,
            key: "projectName",
        },
        {
            title: "Client",
            dataIndex: "accountName",
            sorter: false,
            key: "accountName",
        },
        {
            title: "Month",
            dataIndex: "month",
            sorter: false,
            key: "month",
            render: (month: string) => (month ? moment(month).format("MMM YYYY") : ""),
        },
        {
            title: "Percentage",
            dataIndex: "percentAllocation",
            sorter: false,
            key: "percentAllocation",
            render: (percent: number) => `${percent}%`
        },
        {
            title: "Monthly Cost",
            dataIndex: "monthlyCost",
            sorter: false,
            key: "monthlyCost",
            render: (cost: number) => formatCost(cost),
        },
        {
            title: "Actions",
            key: "actions",
            render: (text: any, record: any) => (
                <Space>
                    <Button
                        type="link"
                        onClick={() => editEntityListener(record)}
                        icon={<EditOutlined />}
                    >
                    </Button>
                </Space>
            ),
        },
    ];

    const onChangeTab = (key: string) => {
        setTab(key);
    };
    useEffect(()=>{
        loadList()
    },[tab])

    const selectedColumns = tab === "1" ? columnsMonthWise : columnsProjectWise;

    return (
        <div className="app-page">
            <div className="app-page-header">
                <Row align="middle" justify="space-between" wrap>
                    <Col flex="1" style={{ minWidth: 0 }}>
                        <div className="app-page-title-wrap">
                            <div className="app-page-icon">
                                <CalendarOutlined />
                            </div>
                            <h1 className="app-page-title">Manage Billing Cycles</h1>
                        </div>
                        <p className="app-page-subtitle">
                            View and manage billing cycles (month wise / project wise)
                        </p>
                    </Col>
                    <Col>{headerButtons}</Col>
                </Row>
            </div>
            {filterVisible && (
                <div className="app-filter-bar">
                    <div className="app-filter-header">
                        <div className="app-filter-title">
                            <BiFilterAlt style={{ fontSize: 14 }} />
                            Filters
                        </div>
                        <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => setFilterVisible(false)}
                            title="Close filters"
                            aria-label="Close filters"
                        />
                    </div>
                    <div className="table-filter-form">
                        <GenericFormComponent {...formProps} />
                    </div>
                </div>
            )}

            <ActiveFilterChipsBar
                show={!filterVisible}
                entries={activeFilterEntries}
                onRemove={removeFilter}
                onClearAll={clearAllFilters}
            />

            <div className="app-table-card">
                <Tabs className="table-status-tabs" defaultActiveKey="1" onChange={onChangeTab}>
                    <Tabs.TabPane tab="Month Wise" key="1"></Tabs.TabPane>
                    <Tabs.TabPane tab="Project Wise" key="2"></Tabs.TabPane>
                </Tabs>
                <DataTableComponent
                    pagination={true}
                    loading={loadingBillingCycle}
                    dataSource={billingCycleListData}
                    columns={selectedColumns}
                    onChange={onChangeHandler}
                    defaultPage={0}
                    currentPage={pagination.page}
                    totalRows={pagination?.total}
                    onPageChange={onPageChange}
                />
            </div>
            <Modal
                title="Confirm Delete"
                open={deleteModalVisible}
                onOk={onDeleteConfirm}
                onCancel={onDeleteCancel}
            >
                <p>Are you sure you want to delete this Billing Cycle?</p>
            </Modal>
            {createEditOpen && (
                <CreateEditBillingCycleComponent
                    onClose={() => setCreateEditOpen(false)}
                    visible={createEditOpen}
                    selectedEntity={selectedEntity}
                    onSubmit={createEditSubmit}
                />
            )}
        </div>
    );
};

export default ManageBillingCyclePage;
